import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import './MessageCenterEmailStyle.css'
import moment from 'moment'
import useEmail from './useEmail'
import MoveEmailsToFolderModal from './MoveEmailsToFolderModal.component'
import { EMAIL_LABEL_COLOR_SWATCHES } from './CreateEmailLabelModal.component'
import {
  EmailAttachmentMeta,
  EmailComposerDraft,
  EmailListFilter,
  EmailMailboxId,
  IEmailMessage,
} from '@dataTypes/email.type'
import ButtonGroup from '@components/common/ButtonGroup/ButtonGroup.component'
import { ConfirmationModal, SearchWithFilter, Button } from '@components/common'
import { EmptyState } from '@components/ui'
import EmailComposeForm from '@components/layout/Modals/EmailSender/EmailComposeForm.component'
import MessageCenterPermanentlyDeleteEmailModal from '../MessageCenterPermanentlyDeleteEmailModal.component'
import MessageCenterDeleteEmailOrganizeModal from '../MessageCenterDeleteEmailOrganizeModal.component'
import { useNotificationToast } from '@context/NotificationToastContext'
import {
  createSuccessToast,
  createFailedToast,
  createEmailSentToast,
  createEmailForwardedToast,
  createEmailLabelsAddedToast,
  createEmailMovedToTrashToast,
  createEmailFolderCreatedToast,
  createEmailMovedToFolderToast,
  createEmailRestoredToast,
  createEmailFolderRenamedToast,
  createEmailLabelCreatedToast,
  createEmailLabelRenamedToast,
  createEmailLabelDeletedToast,
  createEmailFolderDeletedToast,
} from '@utils/notificationToast.util'
import { useAppDispatch, useAppSelector } from '@store/hooks'
import {
  clearEmailFolderAssignments,
  createEmailFolder,
  createEmailLabel,
  deleteEmailFolderRemote,
  deleteEmailLabelRemote,
  fetchEmailFolders,
  fetchEmailLabels,
  fetchEmailStarTags,
  fetchEmailMessages,
  fetchEmailSettings,
  sendEmailMessage,
  setEmailLabelsOnMessages,
  updateEmailFolderRemote,
  updateEmailLabelRemote,
} from '@store/actions/email.action'
import {
  setEmailFilters,
  setEmailSearchQuery,
  clearEmailMessagesFolderId,
  deleteEmailLabel,
  setEmailLabelForIds,
  setEmailMailbox,
  upsertSentEmailFromComposer,
  updateEmailLabel,
} from '@store/reducers/email.reducer'

import { FilterSelection } from '@components/common/SearchWithFilter/SearchWithFilter.component'
import { useMessageCenterTabLayer } from '../MessageCenterTabLayer.context'
import { useGlobalLoadingActions } from '@hooks/useGlobalLoading'
import { NotificationPriority } from '@dataTypes/notification.types'
import {
  isEmailBackendEnabled,
  requestGoogleWorkspaceStatus,
} from '@services/api/email.api'
import { getSettingsRouteById } from '@pages/Settings/settingsRoutes'
import { applyShowImagesToEmailHtml } from '@utils/email-html.util'
import { sanitizeEmailHtml } from '@utils/sanitize-html.util'
import { showCommunicationLogNotFoundToast } from '@utils/patientProfileCommunicationNavigation.util'
import downloadAttachmentIcon from '@assets/images/Messages/download_2_fill.svg'
import replyIcon from '@assets/images/Messages/reply.svg'
import replyAllIcon from '@assets/images/Messages/reply_all.svg'
import forwardIcon from '@assets/images/Messages/forward.svg'
import MessageSortDropdown, {
  type MessageSortMenuSection,
} from '../shared/MessageSortDropdown.component'

const PAGE_CONTENT_LOADING = '.page-content'

const resetNewLabelDraft = (): { name: string; color: string } => ({
  name: '',
  color: EMAIL_LABEL_COLOR_SWATCHES[0] ?? '#FE746B',
})

/** Same segmented control as Messages tabs (Contact, Chat, … Email). */
const EMAIL_FILTER_BUTTONS: { key: EmailListFilter; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'read', label: 'Read' },
  { key: 'unread', label: 'Unread' },
  { key: 'attachments', label: 'With attachments' },
]

/** Drafts have no read/unread state in the list UI (Figma: All | With Attachments). */
const DRAFT_FILTER_BUTTONS: { key: EmailListFilter; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'attachments', label: 'With Attachments' },
]

const BADGE_BACKGROUND_OPACITY = 0.14

const toRgba = (hexColor: string, opacity: number): string | null => {
  const hex = hexColor.replace('#', '')
  const normalizedHex =
    hex.length === 3
      ? `${hex[0]}${hex[0]}${hex[1]}${hex[1]}${hex[2]}${hex[2]}`
      : hex

  if (!/^[0-9a-fA-F]{6}$/.test(normalizedHex)) return null

  const r = parseInt(normalizedHex.slice(0, 2), 16)
  const g = parseInt(normalizedHex.slice(2, 4), 16)
  const b = parseInt(normalizedHex.slice(4, 6), 16)
  return `rgba(${r}, ${g}, ${b}, ${opacity})`
}

const getBadgeStyle = (color?: string): React.CSSProperties | undefined => {
  if (!color) return undefined
  const tintedBackground = toRgba(color, BADGE_BACKGROUND_OPACITY)
  return {
    borderColor: color,
    color,
    backgroundColor: tintedBackground ?? 'transparent',
  }
}

const LOOKS_LIKE_EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

const parseFromField = (
  from: string,
): { displayName: string; email: string | null } => {
  const trimmed = from.trim()
  const bracketMatch = trimmed.match(/^(.+?)\s*<([^>]+)>$/)
  if (bracketMatch) {
    return {
      displayName: bracketMatch[1].trim(),
      email: bracketMatch[2].trim(),
    }
  }
  if (LOOKS_LIKE_EMAIL.test(trimmed)) {
    return { displayName: '', email: trimmed }
  }
  return { displayName: trimmed, email: null }
}

const extractEmailAddress = (from: string): string => {
  const { displayName, email } = parseFromField(from)
  return email ?? displayName
}

const formatOpenedFrom = (
  from: string,
  mailbox: EmailMailboxId,
  settingsDisplayName?: string | null,
): { name: string; email: string | null } => {
  const parsed = parseFromField(from)
  if (parsed.displayName && parsed.email) {
    return { name: parsed.displayName, email: parsed.email }
  }
  if (parsed.email) {
    const displayName = settingsDisplayName?.trim()
    if ((mailbox === 'sent' || from === 'You') && displayName) {
      return { name: displayName, email: parsed.email }
    }
    return { name: parsed.email, email: null }
  }
  return { name: parsed.displayName, email: null }
}

/** Draft list shows recipients as "First, Last" (or joined first names), not the sender. */
const formatDraftRecipientLabel = (row: IEmailMessage): string => {
  const fromComposeTo = row.composeDraft?.to?.filter(Boolean)
  const fromLegacy = row.composeDraft?.recipientEmail
    ? [row.composeDraft.recipientEmail]
    : []
  const recipients =
    row.toRecipients?.filter(Boolean) ?? fromComposeTo ?? fromLegacy

  if (recipients.length === 0) return 'No recipients'

  return recipients
    .map((entry) => {
      const parsed = parseFromField(entry)
      if (parsed.displayName) {
        const parts = parsed.displayName.trim().split(/\s+/)
        if (parts.length >= 2) {
          return `${parts[0]}, ${parts.slice(1).join(' ')}`
        }
        return parsed.displayName
      }
      if (parsed.email) {
        return parsed.email.split('@')[0] || parsed.email
      }
      return entry
    })
    .join(', ')
}

const formatSentRecipientLabel = (row: IEmailMessage): string => {
  const recipients = row.toRecipients?.filter(Boolean) ?? []
  if (recipients.length === 0) return row.from || 'No recipients'

  return recipients
    .map((entry) => {
      const parsed = parseFromField(entry)
      return parsed.email ?? parsed.displayName ?? entry
    })
    .join(', ')
}

const formatDraftSubjectLabel = (subject: string): string => {
  const trimmed = subject.trim()
  if (!trimmed || /^(\(no subject\)|no subject)$/i.test(trimmed)) {
    return 'No Subject'
  }
  return trimmed
}

const ATTACHMENT_EXTENSION_LABELS: Record<string, string> = {
  pdf: 'PDF file',
  jpg: 'Image file',
  jpeg: 'Image file',
  png: 'Image file',
  gif: 'Image file',
  webp: 'Image file',
  doc: 'Word document',
  docx: 'Word document',
  xls: 'Excel spreadsheet',
  xlsx: 'Excel spreadsheet',
  csv: 'CSV file',
  txt: 'Text file',
  zip: 'ZIP archive',
}

const ATTACHMENT_MIME_LABELS: Record<string, string> = {
  'application/pdf': 'PDF file',
  'image/jpeg': 'Image file',
  'image/png': 'Image file',
  'image/gif': 'Image file',
  'image/webp': 'Image file',
  'text/plain': 'Text file',
  'text/csv': 'CSV file',
  'application/msword': 'Word document',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
    'Word document',
  'application/vnd.ms-excel': 'Excel spreadsheet',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet':
    'Excel spreadsheet',
  'application/zip': 'ZIP archive',
}

const formatAttachmentTypeLabel = (
  mimeType?: string,
  fileName?: string,
): string | undefined => {
  const normalizedMime = mimeType?.split(';')[0]?.trim().toLowerCase()
  if (normalizedMime) {
    const mimeLabel = ATTACHMENT_MIME_LABELS[normalizedMime]
    if (mimeLabel) return mimeLabel
    if (normalizedMime.startsWith('image/')) return 'Image file'
    if (normalizedMime.startsWith('video/')) return 'Video file'
    if (normalizedMime.startsWith('audio/')) return 'Audio file'
    if (normalizedMime.startsWith('text/')) return 'Text file'
  }

  const extension = fileName?.split('.').pop()?.toLowerCase()
  if (extension && ATTACHMENT_EXTENSION_LABELS[extension]) {
    return ATTACHMENT_EXTENSION_LABELS[extension]
  }

  return normalizedMime ? 'File' : undefined
}

const formatAttachmentMetaLabel = (
  mimeType: string | undefined,
  sizeLabel: string | undefined,
  fileName?: string,
): string => {
  const typeLabel = formatAttachmentTypeLabel(mimeType, fileName)
  if (typeLabel && sizeLabel) return `${typeLabel} - ${sizeLabel}`
  return typeLabel ?? sizeLabel ?? ''
}

type EmailAttachmentCardProps = {
  name: string
  meta: string
  url?: string
}

const EmailAttachmentCard = ({
  name,
  meta,
  url,
}: EmailAttachmentCardProps): React.ReactElement => {
  const content = (
    <>
      <div className="mce-open-attachment-heading">
        <p className="mce-open-attachment-title">{name}</p>
        <img
          src={downloadAttachmentIcon}
          alt=""
          className="mce-open-attachment-download-icon"
          aria-hidden
        />
      </div>
      <span className="mce-open-attachment-meta">{meta}</span>
    </>
  )

  if (url) {
    return (
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        download={name}
        className="mce-open-attachment-item mce-open-attachment-link"
      >
        {content}
      </a>
    )
  }

  return <div className="mce-open-attachment-item">{content}</div>
}

/** Which "nothing to show" state the list renders: unsynced mailbox, filtered-out, or empty folder. */
type EmptyStateVariant = 'sync' | 'results' | 'folder'

type MessageCenterEmailProps = {
  onOpenEmailComposer?: (draft: Partial<EmailComposerDraft>) => void
  composeOpen?: boolean
  composeDraft?: EmailComposerDraft
  onComposeDraftChange?: (draft: Partial<EmailComposerDraft>) => void
  onComposeAutoSaveAndClose?: () => void
  onComposeClose?: () => void
}

function MessageCenterEmail({
  onOpenEmailComposer,
  composeOpen = false,
  composeDraft,
  onComposeDraftChange,
  onComposeAutoSaveAndClose,
  onComposeClose,
}: MessageCenterEmailProps): React.ReactElement {
  const dispatch = useAppDispatch()
  const location = useLocation()
  const navigate = useNavigate()
  const allEmailMessages = useAppSelector((state) => state.email.messages)
  const selectedClinicId = useAppSelector(
    (state) => state.clinic.selectedClinic?.clinic_id,
  )
  const h = useEmail()
  const { showLoading, hideLoading } = useGlobalLoadingActions()
  const { showToast } = useNotificationToast()
  const { setTabLayerActions } = useMessageCenterTabLayer()
  const emailSearchQuery = useAppSelector((state) => state.email.searchQuery)
  const emailFilters = useAppSelector((state) => state.email.filters)
  const emailMessages = useAppSelector((state) => state.email.messages)
  const emailSettings = useAppSelector((state) => state.email.settings)
  const showUnreadIndicator = emailSettings?.show_unread_mail_indicator ?? false

  const emailFilterSelectionList = useMemo((): FilterSelection => {
    const labelNames = new Set<string>()
    emailMessages.forEach((message) => {
      message.labels.forEach((lb) => labelNames.add(lb.name))
    })
    return {
      status: ['Read', 'Unread', 'Starred', 'With attachments'],
      labels: Array.from(labelNames).sort((a, b) => a.localeCompare(b)),
    }
  }, [emailMessages])

  const handleEmailSearch = (
    event: React.ChangeEvent<HTMLInputElement>,
  ): void => {
    dispatch(setEmailSearchQuery(event.target.value))
  }

  const handleEmailFilterChange = (selectedFilters: FilterSelection): void => {
    dispatch(setEmailFilters({ ...emailFilters, ...selectedFilters }))
  }

  const onNewEmail = (): void => {
    onOpenEmailComposer?.({})
  }

  const selectAllRef = useRef<HTMLInputElement>(null)
  const appliedDeepLinkEmailIdRef = useRef<string | null>(null)
  const emailNotFoundToastShownRef = useRef(false)
  const [initialEmailFetchCompleted, setInitialEmailFetchCompleted] =
    useState(false)
  const [googleMailboxLoaded, setGoogleMailboxLoaded] = useState(false)
  const [googleMailboxConnected, setGoogleMailboxConnected] = useState(false)
  const labelEditCardRef = useRef<HTMLDivElement>(null)
  const labelCreateCardRef = useRef<HTMLDivElement>(null)
  const editingLabelIdRef = useRef<string | null>(null)
  const isCreatingLabelRef = useRef(false)
  const [openedEmailId, setOpenedEmailId] = useState<string | null>(null)
  const [openedRecipientsExpanded, setOpenedRecipientsExpanded] = useState(true)
  const [imagesRevealed, setImagesRevealed] = useState(false)
  const [showImageConfirm, setShowImageConfirm] = useState(false)
  const [pendingLinkUrl, setPendingLinkUrl] = useState<string | null>(null)
  const openedEmailBodyRef = useRef<HTMLDivElement>(null)
  const [customFolders, setCustomFolders] = useState(h.customFolders)
  const [customLabels, setCustomLabels] = useState<
    { id: string; name: string; color: string }[]
  >([])

  useEffect(() => {
    setGoogleMailboxLoaded(false)
    setCustomLabels([])
    setCustomFolders([])

    if (isEmailBackendEnabled()) {
      void requestGoogleWorkspaceStatus()
        .then((status) => {
          setGoogleMailboxConnected(Boolean(status?.isConnected))
        })
        .catch(() => {
          setGoogleMailboxConnected(false)
        })
        .finally(() => {
          setGoogleMailboxLoaded(true)
        })
    } else {
      setGoogleMailboxLoaded(true)
    }
    void dispatch(fetchEmailSettings())
    void dispatch(fetchEmailLabels())
      .unwrap()
      .then((labels) => {
        setCustomLabels(labels)
      })
      .catch(() => undefined)
    void dispatch(fetchEmailStarTags())
    void dispatch(fetchEmailFolders())
      .unwrap()
      .then((folders) => {
        if (folders.length === 0) {
          setCustomFolders([])
          return
        }
        setCustomFolders(folders.map((f) => ({ id: f.id, name: f.name })))
      })
      .catch(() => undefined)
  }, [dispatch, selectedClinicId])

  useEffect(() => {
    if (!h.messagesLoading) {
      setInitialEmailFetchCompleted(true)
    }
  }, [h.messagesLoading])

  const emailSettingsPath =
    getSettingsRouteById('comm-email')?.fullPath ??
    '/settings/communications/email'

  const showNoSyncBanner =
    isEmailBackendEnabled() &&
    initialEmailFetchCompleted &&
    googleMailboxLoaded &&
    !h.messagesLoading &&
    (!googleMailboxConnected || allEmailMessages.length === 0)

  /** True when a search term or filter is narrowing the list, so "no results" reads correctly. */
  const listIsNarrowed =
    emailSearchQuery.trim().length > 0 ||
    h.listFilter !== 'all' ||
    (emailFilters?.status?.length ?? 0) > 0 ||
    (emailFilters?.labels?.length ?? 0) > 0

  const emptyVariant: EmptyStateVariant | null = (() => {
    if (h.messagesLoading) return null
    if (h.filteredMessages.length > 0) return null
    if (showNoSyncBanner) return 'sync'
    return listIsNarrowed ? 'results' : 'folder'
  })()

  const showFullEmptyState =
    initialEmailFetchCompleted &&
    googleMailboxLoaded &&
    !h.messagesLoading &&
    allEmailMessages.length === 0 &&
    !composeOpen
  const showSyncFullEmpty =
    showFullEmptyState && isEmailBackendEnabled() && !googleMailboxConnected

  useEffect(() => {
    setTabLayerActions(
      <div className="mc-contact-buttons">
        <SearchWithFilter
          searchIcon="mgc_search_2_line"
          filterIcon="mgc_settings_2_line"
          placeholder="Search"
          searchQuery={emailSearchQuery}
          onChange={handleEmailSearch}
          className="mcc-filter"
          filters={emailFilterSelectionList}
          selectedFilters={emailFilters}
          onFilterChange={handleEmailFilterChange}
          filterLabels={{
            status: 'Status',
            labels: 'Labels',
          }}
        />
        <Button
          size="md"
          className="button-primary"
          onClick={onNewEmail}
          disabled={showFullEmptyState}
        >
          New email
        </Button>
      </div>,
    )
    return () => setTabLayerActions(null)
  }, [
    emailFilterSelectionList,
    emailFilters,
    emailSearchQuery,
    onOpenEmailComposer,
    setTabLayerActions,
    showFullEmptyState,
  ])

  const [isCreatingLabel, setIsCreatingLabel] = useState(false)
  const [newLabelName, setNewLabelName] = useState('')
  const [newLabelColor, setNewLabelColor] = useState(
    EMAIL_LABEL_COLOR_SWATCHES[0] ?? '#FE746B',
  )
  const [editingFolderId, setEditingFolderId] = useState<string | null>(null)
  const [editingFolderName, setEditingFolderName] = useState('')
  const [openFolderMenuId, setOpenFolderMenuId] = useState<string | null>(null)
  const [confirmPermanentDelete, setConfirmPermanentDelete] = useState<{
    show: boolean
    ids: string[]
  }>({ show: false, ids: [] })
  const [moveFolderModalOpen, setMoveFolderModalOpen] = useState(false)
  const [moveFolderFromDetail, setMoveFolderFromDetail] = useState(false)
  const [pendingFolderDelete, setPendingFolderDelete] = useState<{
    id: string
    name: string
  } | null>(null)
  const [openLabelMenuId, setOpenLabelMenuId] = useState<string | null>(null)
  const [editingLabelId, setEditingLabelId] = useState<string | null>(null)
  const [editingLabelName, setEditingLabelName] = useState('')
  const [editingLabelColor, setEditingLabelColor] = useState(
    EMAIL_LABEL_COLOR_SWATCHES[0] ?? '#FE746B',
  )
  const [pendingLabelDelete, setPendingLabelDelete] = useState<{
    id: string
    name: string
  } | null>(null)
  const [showAddLabelMenu, setShowAddLabelMenu] = useState(false)
  const [showOpenedEmailLabelMenu, setShowOpenedEmailLabelMenu] =
    useState(false)

  const emailSortSections: MessageSortMenuSection[] = useMemo(
    () => [
      {
        id: 'field',
        label: 'Sort By',
        selectedId: h.sortBy.toLowerCase(),
        items: h.sortOptions.map((opt) => ({
          id: opt,
          label: opt.charAt(0).toUpperCase() + opt.slice(1),
        })),
      },
    ],
    [h.sortBy, h.sortOptions],
  )

  const handleComposeSendSuccess = (payload: {
    to: string[]
    cc: string[]
    bcc: string[]
    attachments?: EmailAttachmentMeta[]
    subject: string
    body: string
    draftMessageId?: string
  }): void => {
    if (!composeDraft) return
    dispatch(
      upsertSentEmailFromComposer({
        id: `sent-${Date.now()}`,
        payload: {
          ...payload,
          draftMessageId: payload.draftMessageId ?? composeDraft.draftMessageId,
        },
      }),
    )
    const isForward = composeDraft.modalTitle.toLowerCase().includes('forward')
    showToast(isForward ? createEmailForwardedToast() : createEmailSentToast())
    onComposeClose?.()
    const sendAttachments = (payload.attachments ?? [])
      .filter((attachment) => Boolean(attachment.content))
      .map((attachment) => ({
        filename: attachment.name,
        content: attachment.content as string,
        type: attachment.mimeType,
      }))
    void dispatch(
      sendEmailMessage({
        to: payload.to,
        cc: payload.cc,
        bcc: payload.bcc,
        subject: payload.subject,
        body: payload.body,
        draftMessageId: payload.draftMessageId ?? composeDraft.draftMessageId,
        attachments: sendAttachments.length > 0 ? sendAttachments : undefined,
      }),
    )
      .unwrap()
      .then(() => dispatch(fetchEmailMessages()))
      .catch((message: string) => {
        showToast(
          createFailedToast(
            'Failed to send email',
            message || 'The email could not be sent. Please try again.',
          ),
        )
      })
  }

  useEffect(() => {
    const el = selectAllRef.current
    if (!el) return
    el.indeterminate = h.someVisibleSelected && !h.allVisibleSelected
  }, [h.someVisibleSelected, h.allVisibleSelected])

  useEffect(() => {
    editingLabelIdRef.current = editingLabelId
  }, [editingLabelId])

  useEffect(() => {
    isCreatingLabelRef.current = isCreatingLabel
  }, [isCreatingLabel])

  useEffect(() => {
    if (!openFolderMenuId) return
    const handleClickAway = (): void => {
      setOpenFolderMenuId(null)
    }
    document.addEventListener('click', handleClickAway)
    return () => document.removeEventListener('click', handleClickAway)
  }, [openFolderMenuId])

  useEffect(() => {
    if (!openLabelMenuId) return
    const handleClickAway = (): void => {
      setOpenLabelMenuId(null)
    }
    document.addEventListener('click', handleClickAway)
    return () => document.removeEventListener('click', handleClickAway)
  }, [openLabelMenuId])

  useEffect(() => {
    if (!showAddLabelMenu) return
    const handleClickAway = (): void => {
      setShowAddLabelMenu(false)
    }
    document.addEventListener('click', handleClickAway)
    return () => document.removeEventListener('click', handleClickAway)
  }, [showAddLabelMenu])

  useEffect(() => {
    if (!showOpenedEmailLabelMenu) return
    const handleClickAway = (): void => {
      setShowOpenedEmailLabelMenu(false)
    }
    document.addEventListener('click', handleClickAway)
    return () => document.removeEventListener('click', handleClickAway)
  }, [showOpenedEmailLabelMenu])

  useEffect(() => {
    setShowOpenedEmailLabelMenu(false)
    setOpenedRecipientsExpanded(true)
  }, [openedEmailId])

  useEffect(() => {
    const state = location.state as {
      emailId?: string
      previousPage?: unknown
    } | null
    const emailId = state?.emailId
    if (!emailId) {
      appliedDeepLinkEmailIdRef.current = null
      emailNotFoundToastShownRef.current = false
      return
    }
    if (appliedDeepLinkEmailIdRef.current === emailId) return
    if (h.messagesLoading || !initialEmailFetchCompleted) return

    const previousPage = state?.previousPage
    const clearDeepLinkState = (): void => {
      navigate(location.pathname, {
        replace: true,
        state: previousPage ? { previousPage } : null,
      })
    }

    const row = allEmailMessages.find((message) => message.id === emailId)
    if (!row) {
      if (!emailNotFoundToastShownRef.current) {
        emailNotFoundToastShownRef.current = true
        showCommunicationLogNotFoundToast(showToast, 'email')
      }
      appliedDeepLinkEmailIdRef.current = emailId
      clearDeepLinkState()
      return
    }

    appliedDeepLinkEmailIdRef.current = emailId
    if (h.activeMailbox !== row.mailbox) {
      dispatch(setEmailMailbox(row.mailbox))
    }
    setOpenedEmailId(emailId)
    clearDeepLinkState()
  }, [
    allEmailMessages,
    dispatch,
    h.activeMailbox,
    h.messagesLoading,
    initialEmailFetchCompleted,
    location.pathname,
    location.state,
    navigate,
    showToast,
  ])

  const topMailboxes = h.primaryMailboxes.slice(0, 4)
  const restMailboxes = h.primaryMailboxes.slice(4)
  const handleMailboxClick = (mailbox: typeof h.activeMailbox): void => {
    if (composeOpen) {
      onComposeAutoSaveAndClose?.()
    }
    setOpenedEmailId(null)
    h.handleMailbox(mailbox)
    // Drafts only expose All / With Attachments — clear Read/Unread if active.
    if (
      mailbox === 'drafts' &&
      (h.listFilter === 'read' || h.listFilter === 'unread')
    ) {
      h.handleListFilter('all')
    }
  }
  const handleCustomFolderNav = (folderId: string): void => {
    if (composeOpen) {
      onComposeAutoSaveAndClose?.()
    }
    setOpenedEmailId(null)
    h.handleCustomFolder(folderId)
  }
  const handleCustomLabelNav = (labelId: string): void => {
    if (composeOpen) {
      onComposeAutoSaveAndClose?.()
    }
    setOpenedEmailId(null)
    h.handleCustomLabel(labelId)
  }

  const handleOpenEmailRow = (rowId: string): void => {
    const row = h.filteredMessages.find((m) => m.id === rowId)
    if (
      row &&
      row.mailbox === 'drafts' &&
      row.composeDraft &&
      onOpenEmailComposer
    ) {
      onOpenEmailComposer({
        ...row.composeDraft,
        draftMessageId: row.composeDraft.draftMessageId ?? row.id,
        modalTitle: 'Edit draft',
      })
      return
    }
    if (!row?.isRead) {
      h.setReadStatus([rowId], true)
    }
    setOpenedEmailId(rowId)
  }

  const openedEmail = useMemo(
    () => h.filteredMessages.find((item) => item.id === openedEmailId) ?? null,
    [h.filteredMessages, openedEmailId],
  )
  const openedToText =
    openedEmail?.toRecipients?.length && openedEmail.toRecipients.length > 0
      ? openedEmail.toRecipients.join(', ')
      : '—'
  const openedFrom = openedEmail
    ? formatOpenedFrom(
        openedEmail.from,
        openedEmail.mailbox,
        emailSettings?.display_name,
      )
    : null
  const openedHasCcBcc =
    (openedEmail?.ccRecipients?.length ?? 0) > 0 ||
    (openedEmail?.bccRecipients?.length ?? 0) > 0
  const openedCcText = (openedEmail?.ccRecipients ?? []).join(', ')
  const openedBccText = (openedEmail?.bccRecipients ?? []).join(', ')
  const openedBodyHtml = openedEmail?.bodyHtml?.trim() ?? ''
  const processedOpenedBodyHtml = useMemo(
    () =>
      sanitizeEmailHtml(
        applyShowImagesToEmailHtml(
          openedBodyHtml,
          emailSettings?.show_images,
          imagesRevealed,
        ),
      ),
    [openedBodyHtml, emailSettings, imagesRevealed],
  )

  useEffect(() => {
    setImagesRevealed(false)
    setShowImageConfirm(false)
    setPendingLinkUrl(null)
  }, [openedEmailId])

  useEffect(() => {
    const node = openedEmailBodyRef.current
    if (!node) return undefined

    const isImagePlaceholder = (target: EventTarget | null): boolean =>
      target instanceof Element &&
      Boolean(target.closest('[data-email-image-placeholder]'))

    const handleClick = (event: MouseEvent): void => {
      const target = event.target
      if (isImagePlaceholder(target)) {
        event.preventDefault()
        setShowImageConfirm(true)
        return
      }
      if (!emailSettings?.confirm_link_urls) return
      const anchor =
        target instanceof Element ? target.closest('a[href]') : null
      if (!(anchor instanceof HTMLAnchorElement)) return
      const href = anchor.getAttribute('href')?.trim()
      if (!href || href.startsWith('#') || href.startsWith('mailto:')) return
      event.preventDefault()
      setPendingLinkUrl(href)
    }

    const handleKeyDown = (event: KeyboardEvent): void => {
      if (event.key !== 'Enter' && event.key !== ' ') return
      if (isImagePlaceholder(event.target)) {
        event.preventDefault()
        setShowImageConfirm(true)
      }
    }

    node.addEventListener('click', handleClick)
    node.addEventListener('keydown', handleKeyDown)
    return () => {
      node.removeEventListener('click', handleClick)
      node.removeEventListener('keydown', handleKeyDown)
    }
  }, [processedOpenedBodyHtml, emailSettings?.confirm_link_urls])

  const selectedRows = useMemo(
    () => h.filteredMessages.filter((row) => h.selectedIds.includes(row.id)),
    [h.filteredMessages, h.selectedIds],
  )
  const hasSelectedRows = selectedRows.length > 0
  const shouldMarkSelectedAsRead = selectedRows.some((row) => !row.isRead)
  const selectedReadActionLabel = shouldMarkSelectedAsRead
    ? 'Mark as read'
    : 'Mark as unread'
  const openedReadActionLabel =
    openedEmail && openedEmail.isRead ? 'Mark as unread' : 'Mark as read'
  const isTrashMailbox = h.activeMailbox === 'trash'
  const trashActionLabel = isTrashMailbox ? 'Restore to inbox' : 'Move to trash'
  const isArchiveMailbox = h.activeMailbox === 'archive'
  const archiveActionLabel = isArchiveMailbox ? 'Unarchive to inbox' : 'Archive'
  const isSpamMailbox = h.activeMailbox === 'spam'
  const isDraftsMailbox = h.activeMailbox === 'drafts'
  const isSentMailbox = h.activeMailbox === 'sent'
  const listFilterButtons = isDraftsMailbox
    ? DRAFT_FILTER_BUTTONS
    : EMAIL_FILTER_BUTTONS
  const spamActionLabel = isSpamMailbox
    ? 'Not spam (Move to Inbox)'
    : 'Report spam'

  const handleRequestDeleteLabel = (id: string, name: string): void => {
    setOpenLabelMenuId(null)
    setPendingLabelDelete({ id, name })
    editingLabelIdRef.current = null
    setEditingLabelId(null)
  }

  const handleConfirmDeleteLabel = (): void => {
    if (!pendingLabelDelete) return
    const { id, name } = pendingLabelDelete
    setPendingLabelDelete(null)

    dispatch(deleteEmailLabel({ id }))
    setCustomLabels((prev) => prev.filter((l) => l.id !== id))
    if (editingLabelId === id) setEditingLabelId(null)

    showToast(createEmailLabelDeletedToast(name))

    void dispatch(deleteEmailLabelRemote({ id }))
      .unwrap()
      .catch(() => {
        void dispatch(fetchEmailMessages())
      })
  }

  const handleStartEditLabel = (id: string): void => {
    const label = customLabels.find((l) => l.id === id)
    if (!label) return
    setOpenLabelMenuId(null)
    setEditingLabelId(id)
    setEditingLabelName(label.name)
    setEditingLabelColor(label.color)
  }

  const handleCancelEditLabel = (): void => {
    editingLabelIdRef.current = null
    setEditingLabelId(null)
  }

  const handleConfirmEditLabel = (): void => {
    if (!editingLabelId) return
    const labelName = editingLabelName.trim()
    if (!labelName) return

    const id = editingLabelId
    editingLabelIdRef.current = null

    dispatch(
      updateEmailLabel({
        id,
        name: labelName,
        color: editingLabelColor,
      }),
    )
    setCustomLabels((prev) =>
      prev.map((l) =>
        l.id === id ? { ...l, name: labelName, color: editingLabelColor } : l,
      ),
    )
    setEditingLabelId(null)

    showToast(createEmailLabelRenamedToast(labelName))

    void dispatch(
      updateEmailLabelRemote({ id, name: labelName, color: editingLabelColor }),
    )
      .unwrap()
      .catch(() => {
        void dispatch(fetchEmailMessages())
      })
  }

  /** Commit when leaving the name field unless focus stays inside the edit card (swatches / cancel). */
  const handleEditLabelInputBlur = (): void => {
    requestAnimationFrame(() => {
      if (!editingLabelIdRef.current) return
      const root = labelEditCardRef.current
      const active = document.activeElement
      if (root && active && root.contains(active)) return
      handleConfirmEditLabel()
    })
  }

  const dismissCreateLabel = (): void => {
    isCreatingLabelRef.current = false
    const draft = resetNewLabelDraft()
    setNewLabelName(draft.name)
    setNewLabelColor(draft.color)
    setIsCreatingLabel(false)
  }

  const submitCreateLabel = (): void => {
    const labelName = newLabelName.trim()
    if (!labelName) return

    isCreatingLabelRef.current = false
    const tempId = `l-${Date.now()}`
    setCustomLabels((prev) => [
      {
        id: tempId,
        name: labelName,
        color: newLabelColor,
      },
      ...prev,
    ])
    showToast(createEmailLabelCreatedToast(labelName))
    const draft = resetNewLabelDraft()
    setNewLabelName(draft.name)
    setNewLabelColor(draft.color)
    setIsCreatingLabel(false)

    void dispatch(createEmailLabel({ name: labelName, color: newLabelColor }))
      .unwrap()
      .then((label) => {
        setCustomLabels((prev) =>
          prev.map((entry) =>
            entry.id === tempId
              ? { id: label.id, name: label.name, color: label.color }
              : entry,
          ),
        )
      })
      .catch(() => {
        setCustomLabels((prev) => prev.filter((entry) => entry.id !== tempId))
      })
  }

  /** Blur / outside-click: create if name present, else close draft. Enter uses submitCreateLabel only (no dismiss). */
  const handleCreateLabelDismissOrSubmit = (): void => {
    if (!newLabelName.trim()) {
      dismissCreateLabel()
      return
    }
    submitCreateLabel()
  }

  const handleCreateLabelInputBlur = (): void => {
    requestAnimationFrame(() => {
      if (!isCreatingLabelRef.current) return
      const root = labelCreateCardRef.current
      const active = document.activeElement
      if (root && active && root.contains(active)) return
      handleCreateLabelDismissOrSubmit()
    })
  }

  const confirmEditLabelRef = useRef<() => void>(() => {})
  confirmEditLabelRef.current = handleConfirmEditLabel

  const createLabelDismissOrSubmitRef = useRef<() => void>(() => {})
  createLabelDismissOrSubmitRef.current = handleCreateLabelDismissOrSubmit

  useEffect(() => {
    if (!editingLabelId && !isCreatingLabel) return
    const onPointerDownCapture = (e: PointerEvent): void => {
      if (editingLabelId) {
        const root = labelEditCardRef.current
        if (root && e.target instanceof Node && root.contains(e.target)) return
        confirmEditLabelRef.current()
        return
      }
      const root = labelCreateCardRef.current
      if (root && e.target instanceof Node && root.contains(e.target)) return
      createLabelDismissOrSubmitRef.current()
    }
    document.addEventListener('pointerdown', onPointerDownCapture, true)
    return () =>
      document.removeEventListener('pointerdown', onPointerDownCapture, true)
  }, [editingLabelId, isCreatingLabel])

  const openMoveFolderFromList = (): void => {
    if (!hasSelectedRows) return
    setMoveFolderFromDetail(false)
    setMoveFolderModalOpen(true)
  }

  const toggleAddLabelMenu = (e: React.MouseEvent<HTMLButtonElement>): void => {
    e.stopPropagation()
    if (!hasSelectedRows) return
    setShowOpenedEmailLabelMenu(false)
    setShowAddLabelMenu((prev) => !prev)
  }

  const toggleOpenedEmailLabelMenu = (
    e: React.MouseEvent<HTMLButtonElement>,
  ): void => {
    e.stopPropagation()
    if (!openedEmail) return
    setShowAddLabelMenu(false)
    setShowOpenedEmailLabelMenu((prev) => !prev)
  }

  const applyLabelToggle = (
    rows: Array<{ id: string; labels: Array<{ id: string }> }>,
    label: { id: string; name: string; color: string },
    scope: 'bulk' | 'single',
  ): void => {
    if (rows.length === 0) return
    const ids = rows.map((row) => row.id)
    const targetChecked = !rows.every((row) =>
      row.labels.some((lb) => lb.id === label.id),
    )
    dispatch(
      setEmailLabelForIds({
        ids,
        label,
        setChecked: targetChecked,
      }),
    )
    showToast(
      targetChecked
        ? createEmailLabelsAddedToast()
        : createSuccessToast(
            'Label removed',
            scope === 'single'
              ? `"${label.name}" has been removed from this email.`
              : `"${label.name}" has been removed from selected emails.`,
          ),
    )

    void dispatch(
      setEmailLabelsOnMessages({
        ids,
        labelId: label.id,
        setChecked: targetChecked,
      }),
    )
      .unwrap()
      .catch(() => {
        void dispatch(fetchEmailMessages())
      })
  }

  const handleToggleLabelForSelected = (label: {
    id: string
    name: string
    color: string
  }): void => {
    if (!hasSelectedRows) return
    applyLabelToggle(selectedRows, label, 'bulk')
  }

  const handleToggleLabelForOpenedEmail = (label: {
    id: string
    name: string
    color: string
  }): void => {
    if (!openedEmail) return
    applyLabelToggle([openedEmail], label, 'single')
  }

  const openMoveFolderFromDetail = (): void => {
    if (!openedEmail) return
    setMoveFolderFromDetail(true)
    setMoveFolderModalOpen(true)
  }

  const resolveMoveToFolderIds = (): string[] => {
    if (moveFolderFromDetail && openedEmail) return [openedEmail.id]
    return selectedRows.map((row) => row.id)
  }

  const handleCloseMoveFolderModal = (): void => {
    setMoveFolderModalOpen(false)
    setMoveFolderFromDetail(false)
  }

  const handleConfirmMoveToFolder = (folderId: string): void => {
    const ids = resolveMoveToFolderIds()
    if (ids.length === 0) return
    const folderName =
      customFolders.find((f) => f.id === folderId)?.name ?? 'folder'
    const shouldClearOpened =
      openedEmailId !== null && ids.includes(openedEmailId)
    handleCloseMoveFolderModal()
    showLoading({ container: PAGE_CONTENT_LOADING })
    window.setTimeout(() => {
      h.moveToCustomFolder(ids, folderId)
      hideLoading(PAGE_CONTENT_LOADING)
      if (shouldClearOpened) {
        setOpenedEmailId(null)
      }
      showToast(createEmailMovedToFolderToast(folderName))
    }, 900)
  }

  const handleReply = (): void => {
    if (!onOpenEmailComposer || !openedEmail) return
    const to = extractEmailAddress(openedEmail.from)
    const subj = /^re:\s/i.test(openedEmail.subject.trim())
      ? openedEmail.subject
      : `Re: ${openedEmail.subject}`
    const quoted = `<p><br></p><blockquote style="border-left:2px solid var(--gray-200);padding-left:12px;margin:8px 0;color:var(--gray-500);"><p>${openedEmail.snippet}</p></blockquote>`
    const cc = openedEmail.ccRecipients ? [...openedEmail.ccRecipients] : []
    const bcc = openedEmail.bccRecipients ? [...openedEmail.bccRecipients] : []
    onOpenEmailComposer({
      recipientEmail: to,
      to: [to],
      cc,
      bcc,
      subject: subj,
      body: quoted,
      modalTitle: 'Reply',
    })
  }

  const handleReplyAll = (): void => {
    if (!onOpenEmailComposer || !openedEmail) return
    const sender = extractEmailAddress(openedEmail.from)
    const subj = /^re:\s/i.test(openedEmail.subject.trim())
      ? openedEmail.subject
      : `Re: ${openedEmail.subject}`
    const quoted = `<p><br></p><blockquote style="border-left:2px solid var(--gray-200);padding-left:12px;margin:8px 0;color:var(--gray-500);"><p>${openedEmail.snippet}</p></blockquote>`
    const originalTo = openedEmail.toRecipients ?? []
    const toAll = Array.from(new Set([sender, ...originalTo]))
    const ccAll = openedEmail.ccRecipients ?? []
    const bccAll = openedEmail.bccRecipients ?? []
    onOpenEmailComposer({
      recipientEmail: sender,
      to: toAll,
      cc: ccAll,
      bcc: bccAll,
      expandCcRow: ccAll.length > 0,
      expandBccRow: bccAll.length > 0,
      subject: subj,
      body: quoted,
      modalTitle: 'Reply all',
    })
  }

  const handleForward = (): void => {
    if (!onOpenEmailComposer || !openedEmail) return
    const subj = /^fwd:\s/i.test(openedEmail.subject.trim())
      ? openedEmail.subject
      : `Fwd: ${openedEmail.subject}`
    const forwarded = `
      <p><br></p>
      <p><strong>Forwarded message</strong></p>
      <p>
        <strong>From:</strong> ${openedEmail.from}
        <br/>
        <strong>Subject:</strong> ${openedEmail.subject}
      </p>
      <p>${openedEmail.snippet}</p>
    `.trim()
    onOpenEmailComposer({
      recipientEmail: '',
      to: [],
      subject: subj,
      body: forwarded,
      modalTitle: 'Forward',
    })
  }

  const handleRefresh = (): void => {
    void h.handleRefresh().then(
      () =>
        showToast(
          createSuccessToast(
            'Email refreshed',
            'Your mailbox has been refreshed.',
          ),
        ),
      (reason: unknown) => {
        const message =
          typeof reason === 'string'
            ? reason
            : 'Something went wrong while loading email.'
        showToast({
          id: `toast-${Date.now()}-${Math.random()}`,
          priority: NotificationPriority.URGENT,
          title: 'Could not refresh inbox',
          date: moment().format('MMMM D, YYYY'),
          time: moment().format('h:mm A'),
          message,
          actionButtonText: '',
        })
      },
    )
  }

  const handleToggleSelectedReadState = (): void => {
    if (!hasSelectedRows) return
    h.setReadStatus(
      selectedRows.map((row) => row.id),
      shouldMarkSelectedAsRead,
    )
    showToast(
      createSuccessToast(
        shouldMarkSelectedAsRead ? 'Marked as read' : 'Marked as unread',
        shouldMarkSelectedAsRead
          ? 'Selected emails have been marked as read.'
          : 'Selected emails have been marked as unread.',
      ),
    )
  }

  const handleToggleOpenedReadState = (): void => {
    if (!openedEmail) return
    const nextIsRead = !openedEmail.isRead
    h.setReadStatus([openedEmail.id], nextIsRead)
    showToast(
      createSuccessToast(
        nextIsRead ? 'Marked as read' : 'Marked as unread',
        nextIsRead
          ? 'The email has been marked as read.'
          : 'The email has been marked as unread.',
      ),
    )
  }

  const handleCreateFolder = (): void => {
    const nextId = `f-${Date.now()}`
    const newName = 'New folder'
    setCustomFolders((prev) => [{ id: nextId, name: newName }, ...prev])
    setEditingFolderId(nextId)
    setEditingFolderName(newName)
    if (!h.foldersOpen) {
      h.setFoldersOpen(true)
    }
  }

  const handleStartRenameFolder = (id: string, name: string): void => {
    setOpenFolderMenuId(null)
    setEditingFolderId(id)
    setEditingFolderName(name)
  }

  const handleCommitRenameFolder = (): void => {
    if (!editingFolderId) return
    const folderId = editingFolderId
    const nextName = editingFolderName.trim() || 'New folder'
    const currentFolder = customFolders.find((folder) => folder.id === folderId)
    const isNewFolder = folderId.startsWith('f-')

    setCustomFolders((prev) =>
      prev.map((folder) =>
        folder.id === folderId ? { ...folder, name: nextName } : folder,
      ),
    )
    setEditingFolderId(null)

    if (isNewFolder) {
      void dispatch(createEmailFolder({ name: nextName }))
        .unwrap()
        .then((folder) => {
          setCustomFolders((prev) =>
            prev.map((f) =>
              f.id === folderId ? { id: folder.id, name: folder.name } : f,
            ),
          )
          showToast(createEmailFolderCreatedToast(nextName))
        })
        .catch(() => {
          setCustomFolders((prev) => prev.filter((f) => f.id !== folderId))
          showToast(
            createFailedToast(
              'New folder',
              'Failed to create email folder. Please try again.',
            ),
          )
        })
      return
    }

    if (currentFolder && currentFolder.name !== nextName) {
      void dispatch(updateEmailFolderRemote({ id: folderId, name: nextName }))
        .unwrap()
        .then(() => {
          showToast(createEmailFolderRenamedToast(nextName))
        })
        .catch(() => {
          setCustomFolders((prev) =>
            prev.map((folder) =>
              folder.id === folderId
                ? { ...folder, name: currentFolder.name }
                : folder,
            ),
          )
          showToast(
            createFailedToast(
              'Rename folder',
              'Failed to rename email folder. Please try again.',
            ),
          )
        })
    }
  }

  const handleRequestDeleteFolder = (id: string): void => {
    const folder = customFolders.find((f) => f.id === id)
    if (!folder) return
    setOpenFolderMenuId(null)
    setPendingFolderDelete({ id: folder.id, name: folder.name })
  }

  const handleConfirmDeleteFolder = (): void => {
    if (!pendingFolderDelete) return
    const { id, name } = pendingFolderDelete
    setPendingFolderDelete(null)

    dispatch(clearEmailMessagesFolderId(id))
    setCustomFolders((prev) => prev.filter((folder) => folder.id !== id))
    if (editingFolderId === id) {
      setEditingFolderId(null)
      setEditingFolderName('')
    }
    if (h.activeCustomFolderId === id) {
      h.handleCustomFolder(null)
    }

    void dispatch(clearEmailFolderAssignments({ folderId: id }))
      .unwrap()
      .then(() => dispatch(deleteEmailFolderRemote({ id })).unwrap())
      .catch(() => {
        void dispatch(fetchEmailMessages())
        void dispatch(fetchEmailFolders())
          .unwrap()
          .then((folders) =>
            setCustomFolders(folders.map((f) => ({ id: f.id, name: f.name }))),
          )
          .catch(() => undefined)
      })

    showToast(createEmailFolderDeletedToast(name))
  }

  const handleMoveSelectedToTrash = (): void => {
    if (!hasSelectedRows) return
    h.moveToMailbox(
      selectedRows.map((row) => row.id),
      isTrashMailbox ? 'inbox' : 'trash',
    )
    showToast(
      isTrashMailbox
        ? createEmailRestoredToast()
        : createEmailMovedToTrashToast(),
    )
  }

  const handleMoveOpenedToTrash = (): void => {
    if (!openedEmail) return
    h.moveToMailbox([openedEmail.id], isTrashMailbox ? 'inbox' : 'trash')
    setOpenedEmailId(null)
    showToast(
      isTrashMailbox
        ? createEmailRestoredToast()
        : createEmailMovedToTrashToast(),
    )
  }

  const handleMoveSelectedToArchive = (): void => {
    if (!hasSelectedRows) return
    h.moveToMailbox(
      selectedRows.map((row) => row.id),
      isArchiveMailbox ? 'inbox' : 'archive',
    )
    showToast(
      isArchiveMailbox
        ? createEmailRestoredToast()
        : createSuccessToast(
            'Archived',
            'Selected emails have been moved to Archive.',
          ),
    )
  }

  const handleMoveOpenedToArchive = (): void => {
    if (!openedEmail) return
    h.moveToMailbox([openedEmail.id], isArchiveMailbox ? 'inbox' : 'archive')
    setOpenedEmailId(null)
    showToast(
      isArchiveMailbox
        ? createEmailRestoredToast()
        : createSuccessToast(
            'Archived',
            'The email has been moved to Archive.',
          ),
    )
  }

  const handleMoveSelectedToSpam = (): void => {
    if (!hasSelectedRows) return
    h.moveToMailbox(
      selectedRows.map((row) => row.id),
      isSpamMailbox ? 'inbox' : 'spam',
    )
    showToast(
      isSpamMailbox
        ? createEmailRestoredToast()
        : createSuccessToast(
            'Moved to spam',
            'Selected emails have been moved to Spam.',
          ),
    )
  }

  const handleMoveOpenedToSpam = (): void => {
    if (!openedEmail) return
    h.moveToMailbox([openedEmail.id], isSpamMailbox ? 'inbox' : 'spam')
    setOpenedEmailId(null)
    showToast(
      isSpamMailbox
        ? createEmailRestoredToast()
        : createSuccessToast(
            'Moved to spam',
            'The email has been moved to Spam.',
          ),
    )
  }

  const handlePermanentlyDeleteSelected = (): void => {
    if (!hasSelectedRows) return
    setConfirmPermanentDelete({
      show: true,
      ids: selectedRows.map((row) => row.id),
    })
  }

  const handlePermanentlyDeleteOpened = (): void => {
    if (!openedEmail) return
    setConfirmPermanentDelete({ show: true, ids: [openedEmail.id] })
  }

  const handleConfirmPermanentDelete = (): void => {
    const ids = confirmPermanentDelete.ids
    if (ids.length === 0) {
      setConfirmPermanentDelete({ show: false, ids: [] })
      return
    }
    h.permanentlyDelete(ids)
    if (openedEmail && ids.includes(openedEmail.id)) {
      setOpenedEmailId(null)
    }
    showToast(
      createSuccessToast(
        'Permanently delete email',
        `${ids.length} email${ids.length > 1 ? 's were' : ' was'} permanently deleted.`,
      ),
    )
    setConfirmPermanentDelete({ show: false, ids: [] })
  }

  return (
    <div className={`mc-email${showFullEmptyState ? ' mc-email--empty' : ''}`}>
      {showFullEmptyState ? (
        <EmptyState
          className="mc-email-empty"
          iconClass="mgc mgc_mail_fill"
          title={showSyncFullEmpty ? 'No emails synced yet' : 'No emails found'}
          description={
            showSyncFullEmpty
              ? 'Go to email settings to connect and sync mailbox'
              : "You don't have any emails on record yet."
          }
          actionLabel={showSyncFullEmpty ? 'Go to email settings' : 'New email'}
          onAction={
            showSyncFullEmpty ? () => navigate(emailSettingsPath) : onNewEmail
          }
        />
      ) : (
        <>
          <div className="mce-side-panel">
            <div className="mce-side-panel-scrollable">
              <div className="mce-mailbox-list">
                {topMailboxes.map((item) => (
                  <button
                    key={item.key}
                    type="button"
                    className={`mce-folder-item${h.activeMailbox === item.key && !h.activeCustomFolderId && !h.activeLabelId ? ' active' : ''}`}
                    onClick={() => handleMailboxClick(item.key)}
                  >
                    <span className="mce-folder-row">
                      <i className={`mgc ${item.icon}`} aria-hidden />
                      <span className="mce-folder-label">{item.text}</span>
                    </span>
                    {(h.mailboxCounts[item.key] ?? 0) > 0 ? (
                      <span className="mce-folder-count">
                        {h.mailboxCounts[item.key]}
                      </span>
                    ) : null}
                  </button>
                ))}
              </div>
              <div className="mce-divider" role="separator" />
              <div className="mce-mailbox-list">
                {restMailboxes.map((item) => (
                  <button
                    key={item.key}
                    type="button"
                    className={`mce-folder-item${h.activeMailbox === item.key && !h.activeCustomFolderId && !h.activeLabelId ? ' active' : ''}`}
                    onClick={() => handleMailboxClick(item.key)}
                  >
                    <span className="mce-folder-row">
                      <i className={`mgc ${item.icon}`} aria-hidden />
                      <span className="mce-folder-label">{item.text}</span>
                    </span>
                    {(h.mailboxCounts[item.key] ?? 0) > 0 ? (
                      <span className="mce-folder-count">
                        {h.mailboxCounts[item.key]}
                      </span>
                    ) : null}
                  </button>
                ))}
              </div>
              <div className="mce-divider" role="separator" />
              <div className="mce-section">
                <button
                  type="button"
                  className="mce-section-header"
                  onClick={() => h.setFoldersOpen(!h.foldersOpen)}
                >
                  <span className="mce-section-title">
                    <i className="mgc mgc_folder_fill" aria-hidden />
                    <span className="mce-section-title-text">Folders</span>
                    <i
                      className={`mgc mce-section-title-chevron ${h.foldersOpen ? 'mgc_down_small_fill' : 'mgc_right_small_fill'}`}
                      aria-hidden
                    />
                  </span>
                  <span className="mce-section-actions">
                    <span
                      role="presentation"
                      className="mce-add-hit"
                      onClick={(e) => {
                        e.stopPropagation()
                        handleCreateFolder()
                      }}
                    >
                      <i
                        className="mgc mgc_add_circle_line mce-add-icon"
                        aria-hidden
                        title="Add folder"
                      />
                    </span>
                  </span>
                </button>
                {h.foldersOpen && (
                  <div className="mce-section-body">
                    {customFolders.map((f) =>
                      editingFolderId === f.id ? (
                        <div
                          key={f.id}
                          className="mce-nested-item mce-folder-edit-item"
                        >
                          <i className="mgc mgc_folder_line" aria-hidden />
                          <input
                            autoFocus
                            type="text"
                            className="mce-folder-edit-input"
                            value={editingFolderName}
                            onChange={(e) =>
                              setEditingFolderName(e.target.value)
                            }
                            onBlur={handleCommitRenameFolder}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                handleCommitRenameFolder()
                              } else if (e.key === 'Escape') {
                                setEditingFolderId(null)
                              }
                            }}
                          />
                        </div>
                      ) : (
                        <div
                          key={f.id}
                          className={`mce-nested-item mce-folder-row-item${h.activeCustomFolderId === f.id ? ' mce-custom-folder-active' : ''}`}
                        >
                          <button
                            type="button"
                            className="mce-folder-row-btn mce-folder-nav-btn"
                            onClick={() => handleCustomFolderNav(f.id)}
                            onDoubleClick={(e) => {
                              e.preventDefault()
                              handleStartRenameFolder(f.id, f.name)
                            }}
                            title="Open folder — double-click to rename"
                          >
                            <i className="mgc mgc_folder_line" aria-hidden />
                            <span className="mce-folder-nav-label">
                              {f.name}
                            </span>
                          </button>
                          <div
                            className={`mce-folder-menu-wrap${openFolderMenuId === f.id ? ' is-open' : ''}`}
                          >
                            {(h.folderCounts[f.id] ?? 0) > 0 && (
                              <span className="mce-folder-count mce-folder-count-inline">
                                {h.folderCounts[f.id]}
                              </span>
                            )}
                            <button
                              type="button"
                              className="mce-folder-menu-btn"
                              aria-label="Folder options"
                              onClick={(e) => {
                                e.stopPropagation()
                                setOpenFolderMenuId((prev) =>
                                  prev === f.id ? null : f.id,
                                )
                              }}
                            >
                              <i className="mgc mgc_more_2_fill" aria-hidden />
                            </button>
                            {openFolderMenuId === f.id && (
                              <div
                                className="mce-folder-menu-dropdown"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <button
                                  type="button"
                                  className="mce-folder-menu-item"
                                  onClick={() =>
                                    handleStartRenameFolder(f.id, f.name)
                                  }
                                >
                                  <i
                                    className="mgc mgc_edit_2_line"
                                    aria-hidden
                                  />
                                  <span>Rename</span>
                                </button>
                                <button
                                  type="button"
                                  className="mce-folder-menu-item mce-folder-menu-item--danger"
                                  onClick={() =>
                                    handleRequestDeleteFolder(f.id)
                                  }
                                >
                                  <i
                                    className="mgc mgc_delete_2_line"
                                    aria-hidden
                                  />
                                  <span>Delete folder</span>
                                </button>
                              </div>
                            )}
                          </div>
                        </div>
                      ),
                    )}
                  </div>
                )}
              </div>
              <div className="mce-divider" role="separator" />
              <div className="mce-section">
                <button
                  type="button"
                  className="mce-section-header"
                  onClick={() => h.setLabelsOpen(!h.labelsOpen)}
                >
                  <span className="mce-section-title">
                    <i className="mgc mgc_tag_2_fill" aria-hidden />
                    <span className="mce-section-title-text">Labels</span>
                    <i
                      className={`mgc mce-section-title-chevron ${h.labelsOpen ? 'mgc_down_small_fill' : 'mgc_right_small_fill'}`}
                      aria-hidden
                    />
                  </span>
                  <span className="mce-section-actions">
                    <span
                      role="presentation"
                      className="mce-add-hit"
                      onClick={(e) => {
                        e.stopPropagation()
                        if (!h.labelsOpen) {
                          h.setLabelsOpen(true)
                        }
                        const draft = resetNewLabelDraft()
                        setNewLabelName(draft.name)
                        setNewLabelColor(draft.color)
                        setIsCreatingLabel(true)
                      }}
                    >
                      <i
                        className="mgc mgc_add_circle_line mce-add-icon"
                        aria-hidden
                        title="Add label"
                      />
                    </span>
                  </span>
                </button>
                {h.labelsOpen && (
                  <div className="mce-section-body">
                    {isCreatingLabel && (
                      <div
                        ref={labelCreateCardRef}
                        className="mce-create-label-inline"
                      >
                        <div className="mce-create-label-inline-input-row">
                          <i
                            className="mgc mgc_tag_2_fill mce-label-tag-icon"
                            style={{ color: newLabelColor }}
                            aria-hidden
                          />
                          <input
                            autoFocus
                            type="text"
                            className="mce-create-label-inline-input"
                            placeholder="Label name"
                            value={newLabelName}
                            onChange={(e) => setNewLabelName(e.target.value)}
                            onBlur={handleCreateLabelInputBlur}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                submitCreateLabel()
                              } else if (e.key === 'Escape') {
                                dismissCreateLabel()
                              }
                            }}
                          />
                          <button
                            type="button"
                            className="mce-label-edit-cancel"
                            aria-label="Cancel new label"
                            onClick={dismissCreateLabel}
                          >
                            <i className="mgc mgc_close_line" aria-hidden />
                          </button>
                        </div>
                        <div className="mce-create-label-inline-color-wrap">
                          <p className="mce-create-label-inline-color-label">
                            Select color
                          </p>
                          <div
                            className="mce-create-label-inline-swatches"
                            role="listbox"
                            aria-label="Select label color"
                          >
                            {EMAIL_LABEL_COLOR_SWATCHES.map((hex) => (
                              <button
                                key={hex}
                                type="button"
                                role="option"
                                aria-selected={newLabelColor === hex}
                                className={`mce-create-label-inline-swatch${newLabelColor === hex ? ' is-selected' : ''}`}
                                style={{ backgroundColor: hex }}
                                aria-label={`Preview color ${hex}`}
                                onClick={() => setNewLabelColor(hex)}
                              />
                            ))}
                          </div>
                        </div>
                      </div>
                    )}
                    {customLabels.map((l) =>
                      editingLabelId === l.id ? (
                        <div
                          key={l.id}
                          ref={labelEditCardRef}
                          className="mce-create-label-inline"
                        >
                          <div className="mce-create-label-inline-input-row">
                            <i
                              className="mgc mgc_tag_2_fill mce-label-tag-icon"
                              style={{ color: editingLabelColor }}
                              aria-hidden
                            />
                            <input
                              autoFocus
                              type="text"
                              className="mce-create-label-inline-input"
                              placeholder="Label name"
                              value={editingLabelName}
                              onChange={(e) =>
                                setEditingLabelName(e.target.value)
                              }
                              onBlur={handleEditLabelInputBlur}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                  handleConfirmEditLabel()
                                } else if (e.key === 'Escape') {
                                  handleCancelEditLabel()
                                }
                              }}
                            />
                            <button
                              type="button"
                              className="mce-label-edit-cancel"
                              aria-label="Cancel edit"
                              onClick={handleCancelEditLabel}
                            >
                              <i className="mgc mgc_close_line" aria-hidden />
                            </button>
                          </div>
                          <div className="mce-create-label-inline-color-wrap">
                            <p className="mce-create-label-inline-color-label">
                              Select color
                            </p>
                            <div
                              className="mce-create-label-inline-swatches"
                              role="listbox"
                              aria-label="Select label color"
                            >
                              {EMAIL_LABEL_COLOR_SWATCHES.map((hex) => (
                                <button
                                  key={hex}
                                  type="button"
                                  role="option"
                                  aria-selected={editingLabelColor === hex}
                                  className={`mce-create-label-inline-swatch${editingLabelColor === hex ? ' is-selected' : ''}`}
                                  style={{ backgroundColor: hex }}
                                  aria-label={`Preview color ${hex}`}
                                  onClick={() => setEditingLabelColor(hex)}
                                />
                              ))}
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div
                          key={l.id}
                          className={`mce-nested-item mce-label-row-item${h.activeLabelId === l.id ? ' mce-custom-label-active' : ''}`}
                        >
                          <button
                            type="button"
                            className="mce-label-row-btn mce-label-nav-btn"
                            onClick={() => handleCustomLabelNav(l.id)}
                            onDoubleClick={(e) => {
                              e.preventDefault()
                              handleStartEditLabel(l.id)
                            }}
                            title="Open label — double-click to edit"
                          >
                            <i
                              className="mgc mgc_tag_2_fill mce-label-tag-icon"
                              style={{ color: l.color }}
                              aria-hidden
                            />
                            <span className="mce-label-nav-label">
                              {l.name}
                            </span>
                          </button>
                          <div
                            className={`mce-label-menu-wrap${openLabelMenuId === l.id ? ' is-open' : ''}`}
                          >
                            {(h.labelCounts[l.id] ?? 0) > 0 && (
                              <span className="mce-folder-count mce-folder-count-inline">
                                {h.labelCounts[l.id]}
                              </span>
                            )}
                            <button
                              type="button"
                              className="mce-label-menu-btn"
                              aria-label="Label options"
                              onClick={(e) => {
                                e.stopPropagation()
                                setOpenLabelMenuId((prev) =>
                                  prev === l.id ? null : l.id,
                                )
                              }}
                            >
                              <i className="mgc mgc_more_2_fill" aria-hidden />
                            </button>
                            {openLabelMenuId === l.id && (
                              <div
                                className="mce-label-menu-dropdown"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <button
                                  type="button"
                                  className="mce-label-menu-item"
                                  onClick={() => handleStartEditLabel(l.id)}
                                >
                                  <i
                                    className="mgc mgc_edit_2_line"
                                    aria-hidden
                                  />
                                  <span>Edit</span>
                                </button>
                                <button
                                  type="button"
                                  className="mce-label-menu-item mce-label-menu-item--danger"
                                  onClick={() =>
                                    handleRequestDeleteLabel(l.id, l.name)
                                  }
                                >
                                  <i
                                    className="mgc mgc_delete_2_line"
                                    aria-hidden
                                  />
                                  <span>Delete</span>
                                </button>
                              </div>
                            )}
                          </div>
                        </div>
                      ),
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="mce-main">
            {composeOpen && composeDraft && onComposeClose ? (
              <div className="mce-compose-shell">
                <EmailComposeForm
                  show={composeOpen}
                  draftMessageId={composeDraft.draftMessageId}
                  recipientEmail={composeDraft.recipientEmail}
                  initialSubject={composeDraft.subject}
                  initialBody={composeDraft.body}
                  initialTo={composeDraft.to}
                  initialCc={composeDraft.cc}
                  initialBcc={composeDraft.bcc}
                  initialExpandCcRow={composeDraft.expandCcRow}
                  initialExpandBccRow={composeDraft.expandBccRow}
                  initialAttachments={composeDraft.attachments}
                  modalTitle={composeDraft.modalTitle}
                  variant="inline"
                  onDraftChange={onComposeDraftChange}
                  onClose={onComposeClose}
                  onSendSuccess={handleComposeSendSuccess}
                />
              </div>
            ) : (
              <>
                {!openedEmail && (
                  <div className="mce-toolbar">
                    <div className="mce-toolbar-left">
                      <label className="mce-select-all">
                        <input
                          ref={selectAllRef}
                          type="checkbox"
                          checked={h.allVisibleSelected}
                          onChange={h.toggleSelectAllVisible}
                        />
                        <span>Select all</span>
                      </label>
                      <div
                        className="mce-toolbar-icons"
                        aria-label="Bulk actions"
                      >
                        <button
                          type="button"
                          className="mce-icon-btn mce-icon-tooltip"
                          data-tooltip="Refresh"
                          aria-label="Refresh"
                          onClick={handleRefresh}
                          disabled={h.messagesLoading}
                        >
                          <i className="mgc mgc_refresh_1_fill" />
                        </button>
                        {isTrashMailbox ? (
                          <>
                            <span className="mce-toolbar-divider" aria-hidden />
                            <button
                              type="button"
                              className="mce-icon-btn mce-icon-tooltip"
                              data-tooltip="Restore to inbox"
                              aria-label="Restore to inbox"
                              onClick={handleMoveSelectedToTrash}
                              disabled={!hasSelectedRows}
                            >
                              <i className="mgc mgc_back_fill" />
                            </button>
                            <span className="mce-toolbar-divider" aria-hidden />
                            <button
                              type="button"
                              className="mce-icon-btn mce-icon-tooltip"
                              data-tooltip="Permanently delete"
                              aria-label="Permanently delete"
                              onClick={handlePermanentlyDeleteSelected}
                              disabled={!hasSelectedRows}
                            >
                              <i className="mgc mgc_delete_2_fill" />
                            </button>
                          </>
                        ) : (
                          <>
                            <span className="mce-toolbar-divider" aria-hidden />
                            <button
                              type="button"
                              className="mce-icon-btn mce-icon-tooltip"
                              data-tooltip={selectedReadActionLabel}
                              aria-label={selectedReadActionLabel}
                              onClick={handleToggleSelectedReadState}
                              disabled={!hasSelectedRows}
                            >
                              <i
                                className={`mgc ${shouldMarkSelectedAsRead ? 'mgc_mail_open_fill' : 'mgc_mail_fill'}`}
                              />
                            </button>
                            <span className="mce-toolbar-divider" aria-hidden />
                            <button
                              type="button"
                              className="mce-icon-btn mce-icon-tooltip"
                              data-tooltip={trashActionLabel}
                              aria-label={trashActionLabel}
                              onClick={handleMoveSelectedToTrash}
                              disabled={!hasSelectedRows}
                            >
                              <i className="mgc mgc_delete_2_fill" />
                            </button>
                            <button
                              type="button"
                              className="mce-icon-btn mce-icon-tooltip"
                              data-tooltip={archiveActionLabel}
                              aria-label={archiveActionLabel}
                              onClick={handleMoveSelectedToArchive}
                              disabled={!hasSelectedRows}
                            >
                              <i className="mgc mgc_archive_fill" />
                            </button>
                            <button
                              type="button"
                              className="mce-icon-btn mce-icon-tooltip"
                              data-tooltip={spamActionLabel}
                              aria-label={spamActionLabel}
                              onClick={handleMoveSelectedToSpam}
                              disabled={!hasSelectedRows}
                            >
                              <i className="mgc mgc_alert_fill" />
                            </button>
                            <span className="mce-toolbar-divider" aria-hidden />
                            <button
                              type="button"
                              className="mce-icon-btn mce-icon-tooltip"
                              data-tooltip="Move to folder"
                              aria-label="Move to folder"
                              onClick={openMoveFolderFromList}
                              disabled={!hasSelectedRows}
                            >
                              <i className="mgc mgc_folder_upload_fill" />
                            </button>
                            <div className="mce-toolbar-menu-wrap">
                              <button
                                type="button"
                                className="mce-icon-btn mce-icon-tooltip"
                                data-tooltip="Add label"
                                aria-label="Add label"
                                onClick={toggleAddLabelMenu}
                                disabled={!hasSelectedRows}
                              >
                                <i className="mgc mgc_tag_2_fill" />
                              </button>
                              {showAddLabelMenu && (
                                <div
                                  className="mce-add-label-menu"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  {customLabels.map((label) => {
                                    const checked = selectedRows.every((row) =>
                                      row.labels.some(
                                        (lb) => lb.id === label.id,
                                      ),
                                    )
                                    return (
                                      <button
                                        key={label.id}
                                        type="button"
                                        className={`mce-add-label-menu-item${checked ? ' is-active' : ''}`}
                                        onClick={() =>
                                          handleToggleLabelForSelected(label)
                                        }
                                      >
                                        <span
                                          className={`mce-add-label-checkbox${checked ? ' is-checked' : ''}`}
                                          aria-hidden
                                        >
                                          {checked ? (
                                            <i
                                              className="mgc mgc_check_line"
                                              aria-hidden
                                            />
                                          ) : null}
                                        </span>
                                        <span className="mce-add-label-item-text">
                                          {label.name}
                                        </span>
                                      </button>
                                    )
                                  })}
                                </div>
                              )}
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                    <div className="mce-toolbar-right">
                      <MessageSortDropdown
                        sections={emailSortSections}
                        onSelect={(_sectionId, itemId) =>
                          h.handleSortChange(itemId)
                        }
                      />
                      <div className="mce-filter-tab-wrap">
                        <ButtonGroup
                          buttons={listFilterButtons.map(({ key, label }) => ({
                            label,
                            value: key,
                          }))}
                          activeTab={h.listFilter}
                          onButtonClick={(value) =>
                            h.handleListFilter(value as EmailListFilter)
                          }
                          className="mce-email-filter-tabs"
                        />
                      </div>
                      <div className="mce-pagination">
                        <button type="button" className="mce-icon-btn" disabled>
                          <i className="mgc mgc_left_line" />
                        </button>
                        <span className="mce-page-label">
                          {h.pageCurrent}/{h.pageTotal}
                        </span>
                        <button type="button" className="mce-icon-btn" disabled>
                          <i className="mgc mgc_right_line" />
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {h.messagesError ? (
                  <div className="mce-fetch-error" role="alert">
                    {h.messagesError}
                  </div>
                ) : null}

                <div className="mce-list">
                  {openedEmail ? (
                    <div className="mce-open-email">
                      <button
                        type="button"
                        className="mce-open-back"
                        onClick={() => setOpenedEmailId(null)}
                      >
                        <i className="mgc mgc_left_line" aria-hidden />
                        <span>Back</span>
                      </button>

                      <div className="mce-open-meta-card">
                        <div className="mce-open-meta-top">
                          <div className="mce-open-email-meta">
                            <h3 className="mce-open-subject">
                              {openedEmail.subject}
                            </h3>
                            {openedEmail.labels.length > 0 ? (
                              <div className="mce-open-label-pills">
                                {openedEmail.labels.map((lb) => (
                                  <span
                                    key={lb.id}
                                    className="mce-pill"
                                    style={getBadgeStyle(lb.color)}
                                  >
                                    {lb.name}
                                  </span>
                                ))}
                              </div>
                            ) : null}
                            <div className="mce-open-from-row">
                              <span className="mce-open-from-name">
                                {openedFrom?.name || '—'}
                              </span>
                              {openedFrom?.email ? (
                                <span className="mce-open-email-address">
                                  &lt;{openedFrom.email}&gt;
                                </span>
                              ) : null}
                            </div>
                            <div className="mce-open-line">
                              <span className="mce-open-label">To:</span>
                              <span className="mce-open-value">
                                {openedToText}
                              </span>
                            </div>
                            {openedHasCcBcc ? (
                              <>
                                <button
                                  type="button"
                                  className="mce-open-recipients-toggle"
                                  onClick={() =>
                                    setOpenedRecipientsExpanded((prev) => !prev)
                                  }
                                >
                                  <span>
                                    {openedRecipientsExpanded ? 'Less' : 'More'}
                                  </span>
                                  <i
                                    className={`mgc ${openedRecipientsExpanded ? 'mgc_up_line' : 'mgc_down_line'}`}
                                    aria-hidden
                                  />
                                </button>
                                {openedRecipientsExpanded ? (
                                  <>
                                    {(openedEmail.ccRecipients?.length ?? 0) >
                                    0 ? (
                                      <div className="mce-open-line">
                                        <span className="mce-open-label">
                                          Cc:
                                        </span>
                                        <span className="mce-open-value">
                                          {openedCcText}
                                        </span>
                                      </div>
                                    ) : null}
                                    {(openedEmail.bccRecipients?.length ?? 0) >
                                    0 ? (
                                      <div className="mce-open-line">
                                        <span className="mce-open-label">
                                          Bcc:
                                        </span>
                                        <span className="mce-open-value">
                                          {openedBccText}
                                        </span>
                                      </div>
                                    ) : null}
                                  </>
                                ) : null}
                              </>
                            ) : null}
                          </div>
                          <time className="mce-open-date">
                            {openedEmail.dateLabel}
                          </time>
                        </div>

                        <div className="mce-open-detail-toolbar">
                          <div className="mce-open-detail-toolbar-group">
                            <button
                              type="button"
                              className={`mce-open-detail-toolbar-btn mce-star${openedEmail.isStarred ? ' starred' : ''}`}
                              title={
                                openedEmail.starTag
                                  ? `${openedEmail.starTag.name} star — click to change`
                                  : 'Star'
                              }
                              aria-label={
                                openedEmail.starTag
                                  ? `${openedEmail.starTag.name} star`
                                  : 'Star'
                              }
                              style={
                                openedEmail.starTag?.color
                                  ? ({
                                      ['--mce-star-color' as string]:
                                        openedEmail.starTag.color,
                                    } as React.CSSProperties)
                                  : undefined
                              }
                              onClick={() => h.toggleStar(openedEmail.id)}
                            >
                              <i
                                className={`mgc ${openedEmail.isStarred ? 'mgc_star_fill' : 'mgc_star_line'}`}
                              />
                            </button>
                            {isTrashMailbox ? (
                              <>
                                <button
                                  type="button"
                                  className="mce-open-detail-toolbar-btn mce-icon-tooltip"
                                  data-tooltip="Restore to inbox"
                                  aria-label="Restore to inbox"
                                  onClick={handleMoveOpenedToTrash}
                                >
                                  <i className="mgc mgc_back_fill" />
                                </button>
                                <button
                                  type="button"
                                  className="mce-open-detail-toolbar-btn mce-icon-tooltip"
                                  data-tooltip="Permanently delete"
                                  aria-label="Permanently delete"
                                  onClick={handlePermanentlyDeleteOpened}
                                >
                                  <i className="mgc mgc_delete_2_fill" />
                                </button>
                                <button
                                  type="button"
                                  className="mce-open-detail-toolbar-btn mce-icon-tooltip"
                                  data-tooltip="Refresh"
                                  aria-label="Refresh"
                                  onClick={handleRefresh}
                                  disabled={h.messagesLoading}
                                >
                                  <i className="mgc mgc_refresh_1_fill" />
                                </button>
                              </>
                            ) : (
                              <>
                                <button
                                  type="button"
                                  className="mce-open-detail-toolbar-btn mce-icon-tooltip"
                                  data-tooltip={trashActionLabel}
                                  aria-label={trashActionLabel}
                                  onClick={handleMoveOpenedToTrash}
                                >
                                  <i className="mgc mgc_delete_2_fill" />
                                </button>
                                <button
                                  type="button"
                                  className="mce-open-detail-toolbar-btn mce-icon-tooltip"
                                  data-tooltip="Refresh"
                                  aria-label="Refresh"
                                  onClick={handleRefresh}
                                  disabled={h.messagesLoading}
                                >
                                  <i className="mgc mgc_refresh_1_fill" />
                                </button>
                                <button
                                  type="button"
                                  className="mce-open-detail-toolbar-btn mce-icon-tooltip"
                                  data-tooltip={archiveActionLabel}
                                  aria-label={archiveActionLabel}
                                  onClick={handleMoveOpenedToArchive}
                                >
                                  <i className="mgc mgc_archive_fill" />
                                </button>
                                <button
                                  type="button"
                                  className="mce-open-detail-toolbar-btn mce-icon-tooltip"
                                  data-tooltip={openedReadActionLabel}
                                  aria-label={openedReadActionLabel}
                                  onClick={handleToggleOpenedReadState}
                                >
                                  <i
                                    className={`mgc ${openedEmail?.isRead ? 'mgc_mail_fill' : 'mgc_mail_open_fill'}`}
                                  />
                                </button>
                                <button
                                  type="button"
                                  className="mce-open-detail-toolbar-btn mce-icon-tooltip"
                                  data-tooltip={spamActionLabel}
                                  aria-label={spamActionLabel}
                                  onClick={handleMoveOpenedToSpam}
                                >
                                  <i className="mgc mgc_alert_fill" />
                                </button>
                                <button
                                  type="button"
                                  className="mce-open-detail-toolbar-btn mce-icon-tooltip"
                                  data-tooltip="Move to folder"
                                  aria-label="Move to folder"
                                  onClick={openMoveFolderFromDetail}
                                >
                                  <i className="mgc mgc_folder_fill" />
                                </button>
                                <div className="mce-toolbar-menu-wrap">
                                  <button
                                    type="button"
                                    className="mce-open-detail-toolbar-btn mce-icon-tooltip"
                                    data-tooltip="Add label"
                                    aria-label="Add label"
                                    onClick={toggleOpenedEmailLabelMenu}
                                  >
                                    <i className="mgc mgc_tag_2_fill" />
                                  </button>
                                  {showOpenedEmailLabelMenu && (
                                    <div
                                      className="mce-add-label-menu"
                                      onClick={(e) => e.stopPropagation()}
                                    >
                                      {customLabels.map((label) => {
                                        const checked = openedEmail.labels.some(
                                          (lb) => lb.id === label.id,
                                        )
                                        return (
                                          <button
                                            key={label.id}
                                            type="button"
                                            className={`mce-add-label-menu-item${checked ? ' is-active' : ''}`}
                                            onClick={() =>
                                              handleToggleLabelForOpenedEmail(
                                                label,
                                              )
                                            }
                                          >
                                            <span
                                              className={`mce-add-label-checkbox${checked ? ' is-checked' : ''}`}
                                              aria-hidden
                                            >
                                              {checked ? (
                                                <i
                                                  className="mgc mgc_check_fill"
                                                  aria-hidden
                                                />
                                              ) : null}
                                            </span>
                                            <span className="mce-add-label-item-text">
                                              {label.name}
                                            </span>
                                          </button>
                                        )
                                      })}
                                    </div>
                                  )}
                                </div>
                              </>
                            )}
                          </div>
                          <div className="mce-open-detail-toolbar-group mce-open-detail-toolbar-group-right">
                            <button
                              type="button"
                              className="mce-open-detail-toolbar-btn mce-icon-tooltip"
                              data-tooltip="Reply"
                              aria-label="Reply"
                              onClick={handleReply}
                            >
                              <img
                                src={replyIcon}
                                alt=""
                                className="mce-open-detail-toolbar-icon"
                                aria-hidden
                              />
                            </button>
                            <button
                              type="button"
                              className="mce-open-detail-toolbar-btn mce-icon-tooltip"
                              data-tooltip="Reply all"
                              aria-label="Reply all"
                              onClick={handleReplyAll}
                            >
                              <img
                                src={replyAllIcon}
                                alt=""
                                className="mce-open-detail-toolbar-icon"
                                aria-hidden
                              />
                            </button>
                            <button
                              type="button"
                              className="mce-open-detail-toolbar-btn mce-icon-tooltip"
                              data-tooltip="Forward"
                              aria-label="Forward"
                              onClick={handleForward}
                            >
                              <img
                                src={forwardIcon}
                                alt=""
                                className="mce-open-detail-toolbar-icon"
                                aria-hidden
                              />
                            </button>
                          </div>
                        </div>
                      </div>

                      <div className="mce-open-content">
                        {processedOpenedBodyHtml ? (
                          <div
                            ref={openedEmailBodyRef}
                            className="mce-open-html"
                            dangerouslySetInnerHTML={{
                              __html: processedOpenedBodyHtml,
                            }}
                          />
                        ) : (
                          <>
                            <p className="mce-open-paragraph">
                              What is Lorem Ipsum?
                            </p>
                            <p className="mce-open-paragraph">
                              Lorem Ipsum is simply dummy text of the printing
                              and typesetting industry. Lorem Ipsum has been the
                              industry&apos;s standard dummy text ever since the
                              1500s.
                            </p>
                            <p className="mce-open-paragraph">
                              Why do we use it?
                            </p>
                            <p className="mce-open-paragraph">
                              It is a long established fact that a reader will
                              be distracted by the readable content of a page
                              when looking at its layout.
                            </p>
                          </>
                        )}
                        {(openedEmail.hasAttachment ||
                          (openedEmail.attachments?.length ?? 0) > 0) && (
                          <div className="mce-open-attachments">
                            <p className="mce-open-attachments-title">
                              Attachments
                            </p>
                            <div className="mce-open-attachments-list">
                              {(openedEmail.attachments?.length ?? 0) > 0 ? (
                                openedEmail.attachments?.map((attachment) => (
                                  <EmailAttachmentCard
                                    key={attachment.id}
                                    name={attachment.name}
                                    url={attachment.url}
                                    meta={formatAttachmentMetaLabel(
                                      attachment.mimeType,
                                      attachment.sizeLabel,
                                      attachment.name,
                                    )}
                                  />
                                ))
                              ) : (
                                <EmailAttachmentCard
                                  name="Attachment"
                                  meta="File attached"
                                />
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  ) : emptyVariant === 'sync' ? (
                    <EmptyState
                      iconClass="mgc mgc_mail_fill"
                      title="No emails synced yet"
                      description="Go to email settings to connect and sync mailbox"
                      actionLabel="Go to email settings"
                      onAction={() => navigate(emailSettingsPath)}
                    />
                  ) : emptyVariant === 'results' ? (
                    <EmptyState
                      iconClass="mgc mgc_mail_fill"
                      title="No emails found"
                      description="No emails match your search."
                    />
                  ) : emptyVariant === 'folder' ? (
                    <EmptyState
                      iconClass="mgc mgc_mail_fill"
                      title="No emails found"
                      description="There are no emails in this folder yet."
                      actionLabel="New email"
                      onAction={onNewEmail}
                    />
                  ) : h.messagesLoading ? null : (
                    h.filteredMessages.map((row) => (
                      <div
                        key={row.id}
                        className={`mce-row${showUnreadIndicator && !row.isRead ? ' unread' : ''}${isDraftsMailbox ? ' mce-row-draft' : ''}`}
                        role="button"
                        tabIndex={0}
                        aria-label={`${isDraftsMailbox ? formatDraftRecipientLabel(row) : row.from}, ${isDraftsMailbox ? formatDraftSubjectLabel(row.subject) : row.subject}`}
                        onClick={() => handleOpenEmailRow(row.id)}
                        onKeyDown={(e) => {
                          if (e.key !== 'Enter' && e.key !== ' ') return
                          if (e.target !== e.currentTarget) return
                          e.preventDefault()
                          handleOpenEmailRow(row.id)
                        }}
                      >
                        <label className="mce-row-check">
                          <input
                            type="checkbox"
                            checked={h.selectedIds.includes(row.id)}
                            onChange={() => h.toggleSelect(row.id)}
                            onClick={(e) => e.stopPropagation()}
                          />
                        </label>
                        {isSpamMailbox ? (
                          <span
                            className="mce-spam-icon"
                            title="Spam"
                            aria-label="Spam"
                          >
                            <i className="mgc mgc_alert_fill" aria-hidden />
                          </span>
                        ) : (
                          <button
                            type="button"
                            className={`mce-star mce-inbox-star${row.isStarred ? ' starred' : ''}`}
                            title={
                              row.starTag
                                ? `${row.starTag.name} star — click to change`
                                : 'Star'
                            }
                            style={
                              row.starTag?.color
                                ? ({
                                    ['--mce-star-color' as string]:
                                      row.starTag.color,
                                  } as React.CSSProperties)
                                : undefined
                            }
                            onClick={(e) => {
                              e.stopPropagation()
                              h.toggleStar(row.id)
                            }}
                          >
                            <i
                              className={`mgc ${row.isStarred ? 'mgc_star_fill' : 'mgc_star_line'}`}
                            />
                          </button>
                        )}
                        <div className="mce-row-body">
                          <div className="mce-row-top">
                            <span className="mce-from">
                              {isDraftsMailbox
                                ? formatDraftRecipientLabel(row)
                                : row.from}
                            </span>
                            {!isDraftsMailbox && row.labels.length > 0 ? (
                              <div className="mce-label-pills">
                                {row.labels.map((lb) => (
                                  <span
                                    key={lb.id}
                                    className="mce-pill"
                                    style={getBadgeStyle(lb.color)}
                                  >
                                    {lb.name}
                                  </span>
                                ))}
                              </div>
                            ) : null}
                          </div>
                          <div className="mce-row-subject">
                            {isDraftsMailbox ? (
                              <span className="mce-subject">
                                {formatDraftSubjectLabel(row.subject)} -
                              </span>
                            ) : (
                              <>
                                <span className="mce-subject">
                                  {row.subject}
                                </span>
                                <span className="mce-snippet">
                                  {' '}
                                  — {row.snippet}
                                </span>
                              </>
                            )}
                          </div>
                        </div>
                        <div className="mce-row-meta">
                          {row.hasAttachment && (
                            <i
                              className="mgc mgc_attachment_fill mce-attach"
                              title="Has attachment"
                            />
                          )}
                          <time className="mce-time">{row.dateLabel}</time>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </>
            )}
          </div>
        </>
      )}
      <MoveEmailsToFolderModal
        show={moveFolderModalOpen}
        folders={customFolders}
        onHide={handleCloseMoveFolderModal}
        onConfirm={handleConfirmMoveToFolder}
      />
      <MessageCenterDeleteEmailOrganizeModal
        show={pendingLabelDelete !== null}
        itemType="label"
        itemName={pendingLabelDelete?.name}
        onCancel={() => setPendingLabelDelete(null)}
        onDelete={handleConfirmDeleteLabel}
      />
      <MessageCenterDeleteEmailOrganizeModal
        show={pendingFolderDelete !== null}
        itemType="folder"
        itemName={pendingFolderDelete?.name}
        onCancel={() => setPendingFolderDelete(null)}
        onDelete={handleConfirmDeleteFolder}
      />
      <MessageCenterPermanentlyDeleteEmailModal
        show={confirmPermanentDelete.show}
        count={confirmPermanentDelete.ids.length}
        onCancel={() => setConfirmPermanentDelete({ show: false, ids: [] })}
        onDelete={handleConfirmPermanentDelete}
      />
      <ConfirmationModal
        show={showImageConfirm}
        onHide={() => setShowImageConfirm(false)}
        title="Show images"
        className="confirmation-modal--alert"
        headerClassName="confirmation-modal__header"
        size="md"
        body={
          <div className="confirmation-modal__body">
            <p className="confirmation-modal__title">Show images?</p>
            <p className="confirmation-modal__message">
              Images in this email are hidden. Do you want to display all images
              in this message?
            </p>
          </div>
        }
        footer={
          <div className="confirmation-modal__footer">
            <Button
              className="button-secondary"
              size="md"
              onClick={() => setShowImageConfirm(false)}
            >
              Cancel
            </Button>
            <Button
              className="button-primary"
              size="md"
              onClick={() => {
                setImagesRevealed(true)
                setShowImageConfirm(false)
              }}
            >
              Confirm
            </Button>
          </div>
        }
      />
      <ConfirmationModal
        show={pendingLinkUrl !== null}
        onHide={() => setPendingLinkUrl(null)}
        title="Open link"
        className="confirmation-modal--alert"
        headerClassName="confirmation-modal__header"
        size="md"
        body={
          <div className="confirmation-modal__body">
            <p className="confirmation-modal__title">Open external link?</p>
            <p className="confirmation-modal__message">
              You are about to open an external link. Continue to{' '}
              {pendingLinkUrl}?
            </p>
          </div>
        }
        footer={
          <div className="confirmation-modal__footer">
            <Button
              className="button-secondary"
              size="md"
              onClick={() => setPendingLinkUrl(null)}
            >
              Cancel
            </Button>
            <Button
              className="button-primary"
              size="md"
              onClick={() => {
                if (pendingLinkUrl) {
                  window.open(pendingLinkUrl, '_blank', 'noopener,noreferrer')
                }
                setPendingLinkUrl(null)
              }}
            >
              Open link
            </Button>
          </div>
        }
      />
    </div>
  )
}

export default MessageCenterEmail
